import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getRequest } from '../../apis/ApiInstence';
import { Calendar, User, ArrowLeft } from 'lucide-react';
import headerBg from '../../Assets/img/9.jpg';

const BlogDetails = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  const assetBaseUrl = useMemo(() => {
    let base = process.env.REACT_APP_BACKEND || '';
    base = base.replace('/khajuraho/', '/');
    if (!base.endsWith('/')) {
      base = `${base}/`;
    }
    return base;
  }, []);

  const formatDate = (value) => {
    if (!value) return 'Recent';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Recent';
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: '2-digit',
      year: 'numeric',
    }).format(date);
  };

  useEffect(() => {
    document.title = 'Blog Details | GRAAHO';
    window.scrollTo(0, 0);
    const fetchBlog = async () => {
      setLoading(true);
      try {
        const response = await getRequest(`blogs/${id}`);
        const blogData = response?.data?.data || response?.data;
        if (blogData) {
          let parsedImages = [];
          if (Array.isArray(blogData.images)) {
            parsedImages = blogData.images;
          } else if (typeof blogData.images === 'string' && blogData.images.trim().length > 0) {
            try {
              const maybeArray = JSON.parse(blogData.images);
              parsedImages = Array.isArray(maybeArray) ? maybeArray : [blogData.images];
            } catch (e) {
              parsedImages = [blogData.images];
            }
          }
          const firstImage = parsedImages.length > 0 ? parsedImages[0] : '';
          const image = firstImage
            ? (firstImage.startsWith('http') ? firstImage : `${assetBaseUrl}${String(firstImage).replace(/^\/+/, '')}`)
            : 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2000&auto=format&fit=crop';
          
          setBlog({
            ...blogData,
            image,
            title: blogData.heading || 'Untitled',
            date: formatDate(blogData.createdAt),
            author: blogData.author || 'GRAAHO Team',
          });
        }
      } catch (error) {
        console.error("Error fetching blog details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [id, assetBaseUrl]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500 font-medium">Loading blog details...</div>;
  }

  if (!blog) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500 font-medium">Blog not found.</div>;
  }

  return (
    <main className="w-full bg-[#f9f9f9] text-gray-900 overflow-hidden font-sans pb-20">
      <section className="relative w-full pt-24 pb-36 sm:pt-32 sm:pb-48 lg:pt-40 lg:pb-56 overflow-hidden bg-secondary">
        <div className="absolute inset-0">
          <img src={blog.image || headerBg} className="w-full h-full object-cover" alt={blog.title} />
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />
        </div>
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 relative z-10 text-center">
          <Link to="/news-media" className="inline-flex items-center gap-2 text-white hover:text-orange-main mb-6 transition-colors font-semibold">
            <ArrowLeft size={20} /> Back to Blogs
          </Link>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.1] tracking-tighter mb-6 max-w-4xl mx-auto">
            {blog.title}
          </h1>
          <div className="flex items-center justify-center gap-6 text-white">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-orange-main" />
              <span className="font-bold tracking-widest text-sm uppercase">{blog.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <User size={16} className="text-orange-main" />
              <span className="font-bold tracking-widest text-sm uppercase">{blog.author}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 md:-mt-32 relative z-20">
        <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-xl">
          <div 
            className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
            style={{ whiteSpace: 'pre-line' }}
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />
        </div>
      </section>
    </main>
  );
};

export default BlogDetails;
